import { useEffect, useRef } from "react";
import { XIcon } from "./Icons.jsx";

/**
 * Dialog shell. Escape and backdrop clicks route through `onClose`, so a
 * caller that needs to warn about unsaved work gets one place to intercept.
 */
export function Modal({ title, subtitle, onClose, children, footer, size = "" }) {
  const bodyRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    // Focus the first sensible control so keyboard users land inside.
    const first = bodyRef.current?.querySelector(
      "input:not([type=hidden]), textarea, select, button"
    );
    first?.focus?.();
  }, []);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={`modal ${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="truncate">{title}</h2>
            {subtitle && <div className="tiny muted">{subtitle}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <XIcon />
          </button>
        </header>

        <div className="modal-body" ref={bodyRef}>
          {children}
        </div>

        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>
  );
}

/** A yes/no prompt that returns through callbacks rather than window.confirm. */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Confirm",
  danger = false,
  onConfirm,
  onCancel,
}) {
  return (
    <Modal title={title} onClose={onCancel} size="narrow"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <div className="grow" />
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="secondary" style={{ lineHeight: "var(--leading-relaxed)" }}>{message}</p>
    </Modal>
  );
}
